import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';

import AspectRatio from 'components/AspectRatio';
import { IMAGE_HOST as CONTENTFUL_IMAGE_HOST } from 'constants/contentful';
import { mediumUp } from 'constants/mediaQueries';
import CloudinaryRemoteFetchImage from 'models/CloudinaryRemoteFetchImage';
import Cloudinary from 'utils/Cloudinary';

const downscaleMinWidth = 40;
const downscaleSliceCount = 15;

const ResponsiveImage = ({
  alt,
  className,
  coverParentNode,
  desktopHeight,
  desktopSrc,
  desktopWidth,
  height,
  lazy,
  mobileHeight,
  mobileSrc,
  mobileWidth,
  sizes,
  src,
  transformations,
  width,
  ratio,
}) => {
  const isMediumUp = useMediaQuery({ query: mediumUp });
  const classes = classnames(
    'ResponsiveImage',
    { 'ResponsiveImage--cover': coverParentNode },
    className
  );

  const [ratioWidth, ratioHeight] = ratio ? ratio.split(':').map(Number) : [null, null];
  const calculatedHeight = ratioHeight ? Math.round((width / ratioWidth) * ratioHeight) : null;

  const effectiveWidth = isMediumUp ? desktopWidth || width : mobileWidth || width;
  const effectiveHeight = isMediumUp
    ? desktopHeight || height || calculatedHeight
    : mobileHeight || height || calculatedHeight;

  const effectiveMobileSrc = mobileSrc || src;
  const effectiveDesktopSrc = desktopSrc || src;

  const responsiveWidths = useMemo(() => {
    let maxWidth = effectiveWidth;
    const widths = [];
    const sliceSize = maxWidth / downscaleSliceCount;

    while (maxWidth >= downscaleMinWidth) {
      widths.push(Math.round(maxWidth));
      maxWidth -= sliceSize;
    }

    return widths;
  }, [effectiveWidth]);

  const buildSrc = (src, width) => {
    const params = { ...transformations, width };

    if (src.includes(CONTENTFUL_IMAGE_HOST)) {
      return Cloudinary.buildAutoUploadUrl(src, params);
    }
    const image = new CloudinaryRemoteFetchImage(src, params);

    return image.src;
  };

  const srcSet = src => responsiveWidths.map(w => `${buildSrc(src, w)} ${w}w`).join(', ');

  if (!src && !mobileSrc && !desktopSrc) {
    return null;
  }

  return (
    <>
      {coverParentNode ? (
        <picture className={classes}>
          <source media="(min-width: 768px)" srcSet={srcSet(effectiveDesktopSrc)} sizes={sizes} />
          <source media="(max-width: 767px)" srcSet={srcSet(effectiveMobileSrc)} sizes={sizes} />
          <img
            width={effectiveWidth}
            height={effectiveHeight}
            sizes={sizes}
            src={buildSrc(effectiveMobileSrc, Math.max(...responsiveWidths))}
            alt={alt}
            loading={lazy ? 'lazy' : 'eager'}
          />
        </picture>
      ) : (
        <AspectRatio width={effectiveWidth} height={effectiveHeight}>
          <picture className={classes}>
            <source media="(min-width: 768px)" srcSet={srcSet(effectiveDesktopSrc)} sizes={sizes} />
            <source media="(max-width: 767px)" srcSet={srcSet(effectiveMobileSrc)} sizes={sizes} />
            <img
              width={effectiveWidth}
              height={effectiveHeight}
              sizes={sizes}
              src={buildSrc(effectiveMobileSrc, Math.max(...responsiveWidths))}
              alt={alt}
              loading={lazy ? 'lazy' : 'eager'}
            />
          </picture>
        </AspectRatio>
      )}

      <style jsx>{`
        .ResponsiveImage {
          display: block;
          width: 100%;
          max-width: 100%;
          height: auto;
          color: transparent;
        }
        .ResponsiveImage--cover,
        .ResponsiveImage img {
          min-width: 100%;
          height: 100%;
          max-height: 100%;
          object-fit: cover;
        }
      `}</style>
    </>
  );
};

ResponsiveImage.propTypes = {
  alt: PropTypes.string,
  className: PropTypes.string,
  coverParentNode: PropTypes.bool,
  desktopHeight: PropTypes.number,
  desktopSrc: PropTypes.string,
  desktopWidth: PropTypes.number,
  height: PropTypes.number,
  lazy: PropTypes.bool,
  mobileHeight: PropTypes.number,
  mobileSrc: PropTypes.string,
  mobileWidth: PropTypes.number,
  sizes: PropTypes.string,
  src: PropTypes.string,
  transformations: PropTypes.object,
  width: PropTypes.number,
  ratio: PropTypes.string,
};

ResponsiveImage.defaultProps = {
  alt: '',
  className: '',
  coverParentNode: false,
  sizes: '100vw',
  src: '',
  transformations: {},
};

export default ResponsiveImage;
